export type CountryCode = 'AU' | 'NZ' | 'US' | 'CA' | 'GB' | 'IE' | 'SG' | 'HK'

type CountryRule = {
  code: CountryCode
  label: string
  currency: string
  regionLabel: string
  defaultRegion?: string
  postalLabel: string
  postalPlaceholder: string
  requireRegion: boolean
  requirePostal: boolean
  regionOptions?: string[]
}

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
]
const CA_PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
]

const COUNTRY_RULES: Record<CountryCode, CountryRule> = {
  AU: {
    code: 'AU',
    label: 'Australia',
    currency: 'AUD',
    regionLabel: 'State / Territory',
    defaultRegion: 'NSW',
    postalLabel: 'Postcode',
    postalPlaceholder: 'e.g. 2000',
    requireRegion: true,
    requirePostal: true,
    regionOptions: AU_STATES,
  },
  NZ: {
    code: 'NZ',
    label: 'New Zealand',
    currency: 'NZD',
    regionLabel: 'Region',
    postalLabel: 'Postcode',
    postalPlaceholder: 'e.g. 6011',
    requireRegion: false,
    requirePostal: true,
  },
  US: {
    code: 'US',
    label: 'United States of America',
    currency: 'USD',
    regionLabel: 'State',
    defaultRegion: 'CA',
    postalLabel: 'ZIP code',
    postalPlaceholder: 'e.g. 94105',
    requireRegion: true,
    requirePostal: true,
    regionOptions: US_STATES,
  },
  CA: {
    code: 'CA',
    label: 'Canada',
    currency: 'CAD',
    regionLabel: 'Province / Territory',
    defaultRegion: 'ON',
    postalLabel: 'Postal code',
    postalPlaceholder: 'e.g. M5V 3L9',
    requireRegion: true,
    requirePostal: true,
    regionOptions: CA_PROVINCES,
  },
  GB: {
    code: 'GB',
    label: 'United Kingdom',
    currency: 'GBP',
    regionLabel: 'Region / County',
    postalLabel: 'Postcode',
    postalPlaceholder: 'e.g. SW1A 1AA',
    requireRegion: false,
    requirePostal: true,
  },
  IE: {
    code: 'IE',
    label: 'Ireland',
    currency: 'EUR',
    regionLabel: 'County',
    postalLabel: 'Eircode',
    postalPlaceholder: 'e.g. D02 X285',
    requireRegion: false,
    requirePostal: true,
  },
  SG: {
    code: 'SG',
    label: 'Singapore',
    currency: 'SGD',
    regionLabel: 'Region',
    postalLabel: 'Postal code',
    postalPlaceholder: 'e.g. 018989',
    requireRegion: false,
    requirePostal: true,
  },
  HK: {
    code: 'HK',
    label: 'Hong Kong',
    currency: 'HKD',
    regionLabel: 'District',
    postalLabel: 'Postal code',
    postalPlaceholder: 'Not used in Hong Kong',
    requireRegion: false,
    requirePostal: false,
  },
}

const COUNTRY_NAME_TO_CODE: Record<string, CountryCode> = {
  AU: 'AU',
  AUSTRALIA: 'AU',
  NZ: 'NZ',
  'NEW ZEALAND': 'NZ',
  US: 'US',
  USA: 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  CA: 'CA',
  CANADA: 'CA',
  GB: 'GB',
  UK: 'GB',
  'UNITED KINGDOM': 'GB',
  IE: 'IE',
  IRELAND: 'IE',
  SG: 'SG',
  SINGAPORE: 'SG',
  HK: 'HK',
  'HONG KONG': 'HK',
  'HONG KONG (PRC)': 'HK',
  'HONG KONG PRC': 'HK',
}

export const COUNTRY_OPTIONS = [
  { code: 'AU' as const, label: COUNTRY_RULES.AU.label },
  { code: 'NZ' as const, label: COUNTRY_RULES.NZ.label },
  { code: 'US' as const, label: COUNTRY_RULES.US.label },
  { code: 'CA' as const, label: COUNTRY_RULES.CA.label },
  { code: 'GB' as const, label: COUNTRY_RULES.GB.label },
  { code: 'IE' as const, label: COUNTRY_RULES.IE.label },
  { code: 'SG' as const, label: COUNTRY_RULES.SG.label },
  { code: 'HK' as const, label: COUNTRY_RULES.HK.label },
]

export const normalizeCountryCode = (value: unknown): CountryCode => {
  const raw = String(value || '').trim().toUpperCase()
  if (!raw) return 'AU'
  return COUNTRY_NAME_TO_CODE[raw] || 'AU'
}

export const getCountryRule = (value: unknown): CountryRule => COUNTRY_RULES[normalizeCountryCode(value)]

export const getCountryLabel = (value: unknown): string => getCountryRule(value).label
export const getCurrencyForCountry = (value: unknown): string => getCountryRule(value).currency
export const getDefaultRegionForCountry = (value: unknown): string => getCountryRule(value).defaultRegion || ''

export const isValidPostalCode = (value: unknown, postal: unknown): boolean => {
  const code = normalizeCountryCode(value)
  const v = String(postal || '').trim()
  if (!COUNTRY_RULES[code].requirePostal) return true
  if (!v) return false
  if (code === 'AU' || code === 'NZ') return /^\d{4}$/.test(v)
  if (code === 'US') return /^\d{5}(-\d{4})?$/.test(v)
  if (code === 'CA') return /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/i.test(v)
  if (code === 'GB') return /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i.test(v)
  if (code === 'IE') return /^([AC-FHKNPRTV-Y]\d{2}|D6W)\s?[0-9AC-FHKNPRTV-Y]{4}$/i.test(v)
  if (code === 'SG') return /^\d{6}$/.test(v)
  return true
}

const onlyDigits = (value: string) => value.replace(/\D/g, '')

export const toE164Phone = (value: unknown, country: unknown): string => {
  const code = normalizeCountryCode(country)
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.startsWith('+')) return `+${onlyDigits(raw)}`
  const digits = onlyDigits(raw)
  if (!digits) return ''
  if (code === 'AU') {
    if (digits.startsWith('0')) return `+61${digits.slice(1)}`
    if (digits.startsWith('61')) return `+${digits}`
    return `+61${digits}`
  }
  if (code === 'NZ') {
    if (digits.startsWith('0')) return `+64${digits.slice(1)}`
    if (digits.startsWith('64')) return `+${digits}`
    return `+64${digits}`
  }
  if (code === 'US' || code === 'CA') {
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
    if (digits.length === 10) return `+1${digits}`
    return `+1${digits}`
  }
  if (code === 'GB') {
    if (digits.startsWith('0')) return `+44${digits.slice(1)}`
    if (digits.startsWith('44')) return `+${digits}`
    return `+44${digits}`
  }
  if (code === 'IE') {
    if (digits.startsWith('0')) return `+353${digits.slice(1)}`
    if (digits.startsWith('353')) return `+${digits}`
    return `+353${digits}`
  }
  if (code === 'SG') {
    if (digits.startsWith('65')) return `+${digits}`
    return `+65${digits}`
  }
  if (code === 'HK') {
    if (digits.startsWith('852')) return `+${digits}`
    return `+852${digits}`
  }
  return `+${digits}`
}

export const isValidPhoneForCountry = (value: unknown, country: unknown): boolean => {
  const code = normalizeCountryCode(country)
  const phone = toE164Phone(value, code)
  if (!phone) return false
  if (code === 'AU') return /^\+61(?:4\d{8}|[2378]\d{8}|1300\d{6})$/.test(phone)
  if (code === 'NZ') return /^\+64[2-9]\d{7,9}$/.test(phone)
  if (code === 'US' || code === 'CA') return /^\+1\d{10}$/.test(phone)
  if (code === 'GB') return /^\+44\d{9,10}$/.test(phone)
  if (code === 'IE') return /^\+353\d{7,9}$/.test(phone)
  if (code === 'SG') return /^\+65\d{8}$/.test(phone)
  if (code === 'HK') return /^\+852\d{8}$/.test(phone)
  return /^\+\d{8,15}$/.test(phone)
}

export const phonePlaceholderForCountry = (value: unknown): string => {
  const code = normalizeCountryCode(value)
  if (code === 'AU') return '+61 or 04…'
  if (code === 'NZ') return '+64 or 02…'
  if (code === 'US' || code === 'CA') return '+1 (555) 123-4567'
  if (code === 'GB') return '+44 or 07…'
  if (code === 'IE') return '+353 or 08…'
  if (code === 'SG') return '+65 8123 4567'
  if (code === 'HK') return '+852 5123 4567'
  return '+<country code> …'
}

export type ProfessionalRegistrationProfession =
  | 'doctor'
  | 'migration'
  | 'legal'
  | 'allied'
  | 'education'
  | 'tech'
  | 'property'
  | 'financial'

type ProfessionalRegistrationFieldRule = {
  fieldLabelRequired: string
  fieldLabelOptional: string
  placeholder: string
  requiredError: string
  documentLabel?: string
  documentUploadLabel?: string
  documentMissingError?: string
}

const PROFESSIONAL_REGISTRATION_RULES_BY_PROFESSION: Record<
  ProfessionalRegistrationProfession,
  Record<CountryCode, ProfessionalRegistrationFieldRule>
> = {
  doctor: {
    AU: {
      fieldLabelRequired: 'AHPRA registration *',
      fieldLabelOptional: 'AHPRA registration (optional)',
      placeholder: 'e.g. MED1234567',
      requiredError: 'Add your AHPRA registration.',
      documentLabel: 'AHPRA certificate/statement',
      documentUploadLabel: 'Upload AHPRA certificate/statement',
      documentMissingError: 'Upload your AHPRA certificate/statement.',
    },
    NZ: {
      fieldLabelRequired: 'Medical Council registration *',
      fieldLabelOptional: 'Medical Council registration (optional)',
      placeholder: 'e.g. Medical Council registration number',
      requiredError: 'Add your Medical Council registration number.',
      documentLabel: 'Medical Council registration evidence',
      documentUploadLabel: 'Upload Medical Council registration evidence',
      documentMissingError: 'Upload your Medical Council registration evidence.',
    },
    US: {
      fieldLabelRequired: 'State medical licence *',
      fieldLabelOptional: 'State medical licence (optional)',
      placeholder: 'e.g. state medical licence number',
      requiredError: 'Add your state medical licence number.',
      documentLabel: 'State medical licence evidence',
      documentUploadLabel: 'Upload state medical licence evidence',
      documentMissingError: 'Upload your state medical licence evidence.',
    },
    CA: {
      fieldLabelRequired: 'Provincial medical licence *',
      fieldLabelOptional: 'Provincial medical licence (optional)',
      placeholder: 'e.g. provincial medical licence number',
      requiredError: 'Add your provincial medical licence number.',
      documentLabel: 'Provincial medical licence evidence',
      documentUploadLabel: 'Upload provincial medical licence evidence',
      documentMissingError: 'Upload your provincial medical licence evidence.',
    },
    GB: {
      fieldLabelRequired: 'GMC registration *',
      fieldLabelOptional: 'GMC registration (optional)',
      placeholder: 'e.g. GMC reference number',
      requiredError: 'Add your GMC registration number.',
      documentLabel: 'GMC registration evidence',
      documentUploadLabel: 'Upload GMC registration evidence',
      documentMissingError: 'Upload your GMC registration evidence.',
    },
    IE: {
      fieldLabelRequired: 'Medical Council registration *',
      fieldLabelOptional: 'Medical Council registration (optional)',
      placeholder: 'e.g. Medical Council registration number',
      requiredError: 'Add your Medical Council registration number.',
      documentLabel: 'Medical Council registration evidence',
      documentUploadLabel: 'Upload Medical Council registration evidence',
      documentMissingError: 'Upload your Medical Council registration evidence.',
    },
    SG: {
      fieldLabelRequired: 'SMC registration *',
      fieldLabelOptional: 'SMC registration (optional)',
      placeholder: 'e.g. SMC registration number',
      requiredError: 'Add your SMC registration number.',
      documentLabel: 'SMC registration evidence',
      documentUploadLabel: 'Upload SMC registration evidence',
      documentMissingError: 'Upload your SMC registration evidence.',
    },
    HK: {
      fieldLabelRequired: 'Medical Council registration *',
      fieldLabelOptional: 'Medical Council registration (optional)',
      placeholder: 'e.g. Medical Council registration number',
      requiredError: 'Add your Medical Council registration number.',
      documentLabel: 'Medical Council registration evidence',
      documentUploadLabel: 'Upload Medical Council registration evidence',
      documentMissingError: 'Upload your Medical Council registration evidence.',
    },
  },
  migration: {
    AU: {
      fieldLabelRequired: 'Migration agent / MARA registration *',
      fieldLabelOptional: 'Migration agent / MARA registration (optional)',
      placeholder: 'e.g. MARN 0000000',
      requiredError: 'Add your MARA registration number.',
    },
    NZ: {
      fieldLabelRequired: 'Immigration Adviser Authority registration *',
      fieldLabelOptional: 'Immigration Adviser Authority registration (optional)',
      placeholder: 'e.g. IAA licence number',
      requiredError: 'Add your Immigration Adviser Authority registration number.',
    },
    US: {
      fieldLabelRequired: 'Immigration adviser registration *',
      fieldLabelOptional: 'Immigration adviser registration (optional)',
      placeholder: 'e.g. EOIR accreditation or state bar number',
      requiredError: 'Add your immigration adviser registration number.',
    },
    CA: {
      fieldLabelRequired: 'CICC licence / registration *',
      fieldLabelOptional: 'CICC licence / registration (optional)',
      placeholder: 'e.g. CICC licence number',
      requiredError: 'Add your CICC licence or registration number.',
    },
    GB: {
      fieldLabelRequired: 'OISC registration *',
      fieldLabelOptional: 'OISC registration (optional)',
      placeholder: 'e.g. OISC registration number',
      requiredError: 'Add your OISC registration number.',
    },
    IE: {
      fieldLabelRequired: 'Immigration adviser registration *',
      fieldLabelOptional: 'Immigration adviser registration (optional)',
      placeholder: 'e.g. immigration adviser registration number',
      requiredError: 'Add your immigration adviser registration number.',
    },
    SG: {
      fieldLabelRequired: 'Immigration adviser registration *',
      fieldLabelOptional: 'Immigration adviser registration (optional)',
      placeholder: 'e.g. immigration adviser registration number',
      requiredError: 'Add your immigration adviser registration number.',
    },
    HK: {
      fieldLabelRequired: 'Immigration adviser registration *',
      fieldLabelOptional: 'Immigration adviser registration (optional)',
      placeholder: 'e.g. immigration adviser registration number',
      requiredError: 'Add your immigration adviser registration number.',
    },
  },
  legal: {
    AU: {
      fieldLabelRequired: 'Practising certificate / Law Society number *',
      fieldLabelOptional: 'Practising certificate / Law Society number (optional)',
      placeholder: 'e.g. Law Society or Bar number',
      requiredError: 'Add your practising certificate or Law Society number.',
    },
    NZ: {
      fieldLabelRequired: 'NZLS practising certificate number *',
      fieldLabelOptional: 'NZLS practising certificate number (optional)',
      placeholder: 'e.g. NZLS practising certificate number',
      requiredError: 'Add your NZLS practising certificate number.',
    },
    US: {
      fieldLabelRequired: 'State Bar number *',
      fieldLabelOptional: 'State Bar number (optional)',
      placeholder: 'e.g. state bar number',
      requiredError: 'Add your State Bar number.',
    },
    CA: {
      fieldLabelRequired: 'Provincial Law Society number *',
      fieldLabelOptional: 'Provincial Law Society number (optional)',
      placeholder: 'e.g. provincial Law Society number',
      requiredError: 'Add your provincial Law Society number.',
    },
    GB: {
      fieldLabelRequired: 'SRA / BSB registration number *',
      fieldLabelOptional: 'SRA / BSB registration number (optional)',
      placeholder: 'e.g. SRA or BSB number',
      requiredError: 'Add your SRA or BSB registration number.',
    },
    IE: {
      fieldLabelRequired: 'Law Society / Bar Council number *',
      fieldLabelOptional: 'Law Society / Bar Council number (optional)',
      placeholder: 'e.g. Law Society or Bar Council number',
      requiredError: 'Add your Law Society or Bar Council number.',
    },
    SG: {
      fieldLabelRequired: 'Law Society registration number *',
      fieldLabelOptional: 'Law Society registration number (optional)',
      placeholder: 'e.g. Law Society registration number',
      requiredError: 'Add your Law Society registration number.',
    },
    HK: {
      fieldLabelRequired: 'Law Society / Bar Association number *',
      fieldLabelOptional: 'Law Society / Bar Association number (optional)',
      placeholder: 'e.g. Law Society or Bar Association number',
      requiredError: 'Add your Law Society or Bar Association number.',
    },
  },
  allied: {
    AU: {
      fieldLabelRequired: 'AHPRA / association registration *',
      fieldLabelOptional: 'AHPRA / association registration (optional)',
      placeholder: 'e.g. AHPRA or association ID',
      requiredError: 'Add your AHPRA or association registration number.',
    },
    NZ: {
      fieldLabelRequired: 'Board / association registration *',
      fieldLabelOptional: 'Board / association registration (optional)',
      placeholder: 'e.g. professional board or association number',
      requiredError: 'Add your board or association registration number.',
    },
    US: {
      fieldLabelRequired: 'State licence / board registration *',
      fieldLabelOptional: 'State licence / board registration (optional)',
      placeholder: 'e.g. state licence number',
      requiredError: 'Add your state licence or board registration number.',
    },
    CA: {
      fieldLabelRequired: 'Provincial licence / college registration *',
      fieldLabelOptional: 'Provincial licence / college registration (optional)',
      placeholder: 'e.g. provincial registration number',
      requiredError: 'Add your provincial licence or college registration number.',
    },
    GB: {
      fieldLabelRequired: 'Regulator registration *',
      fieldLabelOptional: 'Regulator registration (optional)',
      placeholder: 'e.g. HCPC or professional body number',
      requiredError: 'Add your regulator registration number.',
    },
    IE: {
      fieldLabelRequired: 'CORU / regulator registration *',
      fieldLabelOptional: 'CORU / regulator registration (optional)',
      placeholder: 'e.g. CORU/PSI registration number',
      requiredError: 'Add your CORU or regulator registration number.',
    },
    SG: {
      fieldLabelRequired: 'Board / council registration *',
      fieldLabelOptional: 'Board / council registration (optional)',
      placeholder: 'e.g. AHPC/SNB/SPC registration number',
      requiredError: 'Add your board or council registration number.',
    },
    HK: {
      fieldLabelRequired: 'Board / council registration *',
      fieldLabelOptional: 'Board / council registration (optional)',
      placeholder: 'e.g. board or council registration number',
      requiredError: 'Add your board or council registration number.',
    },
  },
  education: {
    AU: {
      fieldLabelRequired: 'Teacher registration / TRN *',
      fieldLabelOptional: 'Teacher registration / TRN (optional)',
      placeholder: 'e.g. TRN or teacher registration number',
      requiredError: 'Add your teacher registration or TRN.',
    },
    NZ: {
      fieldLabelRequired: 'Teaching practising certificate *',
      fieldLabelOptional: 'Teaching practising certificate (optional)',
      placeholder: 'e.g. Teaching Council practising certificate number',
      requiredError: 'Add your teaching practising certificate number.',
    },
    US: {
      fieldLabelRequired: 'Teaching licence / certification *',
      fieldLabelOptional: 'Teaching licence / certification (optional)',
      placeholder: 'e.g. state teaching licence or certification ID',
      requiredError: 'Add your teaching licence or certification ID.',
    },
    CA: {
      fieldLabelRequired: 'Provincial teaching certificate *',
      fieldLabelOptional: 'Provincial teaching certificate (optional)',
      placeholder: 'e.g. provincial teaching certificate number',
      requiredError: 'Add your provincial teaching certificate number.',
    },
    GB: {
      fieldLabelRequired: 'Teacher registration number *',
      fieldLabelOptional: 'Teacher registration number (optional)',
      placeholder: 'e.g. teacher reference / registration number',
      requiredError: 'Add your teacher registration number.',
    },
    IE: {
      fieldLabelRequired: 'Teaching Council registration *',
      fieldLabelOptional: 'Teaching Council registration (optional)',
      placeholder: 'e.g. Teaching Council registration number',
      requiredError: 'Add your Teaching Council registration number.',
    },
    SG: {
      fieldLabelRequired: 'Teacher registration / accreditation *',
      fieldLabelOptional: 'Teacher registration / accreditation (optional)',
      placeholder: 'e.g. teacher registration or accreditation number',
      requiredError: 'Add your teacher registration or accreditation number.',
    },
    HK: {
      fieldLabelRequired: 'Teacher registration / permit *',
      fieldLabelOptional: 'Teacher registration / permit (optional)',
      placeholder: 'e.g. teacher registration or permit number',
      requiredError: 'Add your teacher registration or permit number.',
    },
  },
  tech: {
    AU: {
      fieldLabelRequired: 'Professional certification / registration *',
      fieldLabelOptional: 'Professional certification / registration (optional)',
      placeholder: 'e.g. certification ID',
      requiredError: 'Add your certification or registration ID.',
    },
    NZ: {
      fieldLabelRequired: 'Professional certification / registration *',
      fieldLabelOptional: 'Professional certification / registration (optional)',
      placeholder: 'e.g. certification ID',
      requiredError: 'Add your certification or registration ID.',
    },
    US: {
      fieldLabelRequired: 'State licence / certification ID *',
      fieldLabelOptional: 'State licence / certification ID (optional)',
      placeholder: 'e.g. state licence or certification ID',
      requiredError: 'Add your state licence or certification ID.',
    },
    CA: {
      fieldLabelRequired: 'Provincial licence / certification ID *',
      fieldLabelOptional: 'Provincial licence / certification ID (optional)',
      placeholder: 'e.g. provincial licence or certification ID',
      requiredError: 'Add your provincial licence or certification ID.',
    },
    GB: {
      fieldLabelRequired: 'Professional registration / certification ID *',
      fieldLabelOptional: 'Professional registration / certification ID (optional)',
      placeholder: 'e.g. registration or certification ID',
      requiredError: 'Add your registration or certification ID.',
    },
    IE: {
      fieldLabelRequired: 'Professional registration / certification ID *',
      fieldLabelOptional: 'Professional registration / certification ID (optional)',
      placeholder: 'e.g. registration or certification ID',
      requiredError: 'Add your registration or certification ID.',
    },
    SG: {
      fieldLabelRequired: 'Professional registration / accreditation ID *',
      fieldLabelOptional: 'Professional registration / accreditation ID (optional)',
      placeholder: 'e.g. registration or accreditation ID',
      requiredError: 'Add your registration or accreditation ID.',
    },
    HK: {
      fieldLabelRequired: 'Professional registration / licence ID *',
      fieldLabelOptional: 'Professional registration / licence ID (optional)',
      placeholder: 'e.g. registration or licence ID',
      requiredError: 'Add your registration or licence ID.',
    },
  },
  property: {
    AU: {
      fieldLabelRequired: 'Real estate licence / registration *',
      fieldLabelOptional: 'Real estate licence / registration (optional)',
      placeholder: 'e.g. real estate licence number',
      requiredError: 'Add your real estate licence or registration number.',
    },
    NZ: {
      fieldLabelRequired: 'REA licence number *',
      fieldLabelOptional: 'REA licence number (optional)',
      placeholder: 'e.g. REA licence number',
      requiredError: 'Add your REA licence number.',
    },
    US: {
      fieldLabelRequired: 'State real estate licence number *',
      fieldLabelOptional: 'State real estate licence number (optional)',
      placeholder: 'e.g. state real estate licence number',
      requiredError: 'Add your state real estate licence number.',
    },
    CA: {
      fieldLabelRequired: 'Provincial real estate licence number *',
      fieldLabelOptional: 'Provincial real estate licence number (optional)',
      placeholder: 'e.g. provincial real estate licence number',
      requiredError: 'Add your provincial real estate licence number.',
    },
    GB: {
      fieldLabelRequired: 'Property licence / registration *',
      fieldLabelOptional: 'Property licence / registration (optional)',
      placeholder: 'e.g. property licence or registration number',
      requiredError: 'Add your property licence or registration number.',
    },
    IE: {
      fieldLabelRequired: 'PSRA licence number *',
      fieldLabelOptional: 'PSRA licence number (optional)',
      placeholder: 'e.g. PSRA licence number',
      requiredError: 'Add your PSRA licence number.',
    },
    SG: {
      fieldLabelRequired: 'CEA registration number *',
      fieldLabelOptional: 'CEA registration number (optional)',
      placeholder: 'e.g. CEA registration number',
      requiredError: 'Add your CEA registration number.',
    },
    HK: {
      fieldLabelRequired: 'Estate agent licence number *',
      fieldLabelOptional: 'Estate agent licence number (optional)',
      placeholder: 'e.g. Estate Agents Authority licence number',
      requiredError: 'Add your estate agent licence number.',
    },
  },
  financial: {
    AU: {
      fieldLabelRequired: 'AFSL / financial registration *',
      fieldLabelOptional: 'AFSL / financial registration (optional)',
      placeholder: 'e.g. AFSL or accreditation ID',
      requiredError: 'Add your AFSL or financial registration ID.',
    },
    NZ: {
      fieldLabelRequired: 'FSP registration number *',
      fieldLabelOptional: 'FSP registration number (optional)',
      placeholder: 'e.g. FSP registration number',
      requiredError: 'Add your FSP registration number.',
    },
    US: {
      fieldLabelRequired: 'SEC/FINRA/NMLS registration ID *',
      fieldLabelOptional: 'SEC/FINRA/NMLS registration ID (optional)',
      placeholder: 'e.g. SEC/FINRA/NMLS registration ID',
      requiredError: 'Add your SEC/FINRA/NMLS registration ID.',
    },
    CA: {
      fieldLabelRequired: 'Provincial/CIRO registration ID *',
      fieldLabelOptional: 'Provincial/CIRO registration ID (optional)',
      placeholder: 'e.g. provincial/CIRO registration ID',
      requiredError: 'Add your provincial or CIRO registration ID.',
    },
    GB: {
      fieldLabelRequired: 'FCA reference number *',
      fieldLabelOptional: 'FCA reference number (optional)',
      placeholder: 'e.g. FCA firm/person reference',
      requiredError: 'Add your FCA reference number.',
    },
    IE: {
      fieldLabelRequired: 'Central Bank registration number *',
      fieldLabelOptional: 'Central Bank registration number (optional)',
      placeholder: 'e.g. Central Bank registration number',
      requiredError: 'Add your Central Bank registration number.',
    },
    SG: {
      fieldLabelRequired: 'MAS registration number *',
      fieldLabelOptional: 'MAS registration number (optional)',
      placeholder: 'e.g. MAS representative or firm number',
      requiredError: 'Add your MAS registration number.',
    },
    HK: {
      fieldLabelRequired: 'SFC/HKMA registration number *',
      fieldLabelOptional: 'SFC/HKMA registration number (optional)',
      placeholder: 'e.g. SFC/HKMA registration number',
      requiredError: 'Add your SFC/HKMA registration number.',
    },
  },
}

export const getProfessionalRegistrationFieldRule = (
  profession: ProfessionalRegistrationProfession,
  country: unknown
): ProfessionalRegistrationFieldRule =>
  PROFESSIONAL_REGISTRATION_RULES_BY_PROFESSION[profession][normalizeCountryCode(country)]

type BusinessTaxIdRule = {
  shortLabel: string
  fieldLabel: string
  placeholder: string
}

const BUSINESS_TAX_ID_RULES_BY_COUNTRY: Record<CountryCode, BusinessTaxIdRule> = {
  AU: {
    shortLabel: 'ABN',
    fieldLabel: 'ABN (optional)',
    placeholder: '11 digit ABN',
  },
  NZ: {
    shortLabel: 'NZBN / GST number',
    fieldLabel: 'NZBN / GST number (optional)',
    placeholder: 'e.g. 9429041234567',
  },
  US: {
    shortLabel: 'EIN / Tax ID',
    fieldLabel: 'EIN / Tax ID (optional)',
    placeholder: 'e.g. 12-3456789',
  },
  CA: {
    shortLabel: 'Business number / GST/HST number',
    fieldLabel: 'Business number / GST/HST number (optional)',
    placeholder: 'e.g. 123456789 RT0001',
  },
  GB: {
    shortLabel: 'VAT / UTR number',
    fieldLabel: 'VAT / UTR number (optional)',
    placeholder: 'e.g. GB123456789',
  },
  IE: {
    shortLabel: 'VAT / Tax Reference number',
    fieldLabel: 'VAT / Tax Reference number (optional)',
    placeholder: 'e.g. IE1234567A',
  },
  SG: {
    shortLabel: 'UEN / GST number',
    fieldLabel: 'UEN / GST number (optional)',
    placeholder: 'e.g. 201912345A',
  },
  HK: {
    shortLabel: 'Business Registration number',
    fieldLabel: 'Business Registration number (optional)',
    placeholder: 'e.g. 12345678',
  },
}

const GENERIC_BUSINESS_TAX_ID_REGEX = /^[A-Z0-9][A-Z0-9\\-\\/ ]{1,31}$/

export const getBusinessTaxIdRuleForCountry = (value: unknown): BusinessTaxIdRule =>
  BUSINESS_TAX_ID_RULES_BY_COUNTRY[normalizeCountryCode(value)]

export const getBusinessTaxIdShortLabel = (value: unknown): string =>
  getBusinessTaxIdRuleForCountry(value).shortLabel

export const getBusinessTaxIdFieldLabel = (value: unknown): string =>
  getBusinessTaxIdRuleForCountry(value).fieldLabel

export const getBusinessTaxIdPlaceholder = (value: unknown): string =>
  getBusinessTaxIdRuleForCountry(value).placeholder

export const normalizeBusinessTaxId = (value: unknown, country: unknown): string => {
  const code = normalizeCountryCode(country)
  const raw = String(value || '').replace(/\s+/g, ' ').trim()
  if (!raw) return ''
  if (code === 'AU') return raw.replace(/\D/g, '')
  return raw.toUpperCase()
}

export const isValidBusinessTaxId = (value: unknown, country: unknown): boolean => {
  const code = normalizeCountryCode(country)
  const normalized = normalizeBusinessTaxId(value, code)
  if (!normalized) return false
  if (code === 'AU') return /^\d{11}$/.test(normalized)
  return GENERIC_BUSINESS_TAX_ID_REGEX.test(normalized)
}

export const businessTaxIdValidationMessage = (value: unknown): string => {
  const code = normalizeCountryCode(value)
  if (code === 'AU') return 'ABN must be 11 digits or leave it blank.'
  return `${getBusinessTaxIdShortLabel(code)} can include letters, numbers, spaces, '-' or '/'.`
}

type TaxRule = {
  label: string
  percent: number
  inclusive: boolean
  requiresAbn: boolean
  description: string
}

const TAX_RULES_BY_COUNTRY: Record<CountryCode, TaxRule> = {
  AU: { label: 'GST', percent: 10, inclusive: true, requiresAbn: true, description: 'Goods and Services Tax' },
  NZ: { label: 'GST', percent: 15, inclusive: true, requiresAbn: false, description: 'Goods and Services Tax' },
  US: { label: 'Sales Tax', percent: 0, inclusive: false, requiresAbn: false, description: 'State-level sales tax varies by jurisdiction' },
  CA: { label: 'HST/GST', percent: 0, inclusive: false, requiresAbn: false, description: 'Province-level GST/HST/PST varies by jurisdiction' },
  GB: { label: 'VAT', percent: 20, inclusive: true, requiresAbn: false, description: 'Value added tax' },
  IE: { label: 'VAT', percent: 23, inclusive: true, requiresAbn: false, description: 'Value added tax' },
  SG: { label: 'GST', percent: 9, inclusive: true, requiresAbn: false, description: 'Goods and Services Tax' },
  HK: { label: 'Tax', percent: 0, inclusive: false, requiresAbn: false, description: 'No GST/VAT configured' },
}

const REGION_CODE_ALIASES_BY_COUNTRY: Partial<Record<CountryCode, Record<string, string>>> = {
  US: {
    ALABAMA: 'AL',
    ALASKA: 'AK',
    ARIZONA: 'AZ',
    ARKANSAS: 'AR',
    CALIFORNIA: 'CA',
    COLORADO: 'CO',
    CONNECTICUT: 'CT',
    DELAWARE: 'DE',
    FLORIDA: 'FL',
    GEORGIA: 'GA',
    HAWAII: 'HI',
    IDAHO: 'ID',
    ILLINOIS: 'IL',
    INDIANA: 'IN',
    IOWA: 'IA',
    KANSAS: 'KS',
    KENTUCKY: 'KY',
    LOUISIANA: 'LA',
    MAINE: 'ME',
    MARYLAND: 'MD',
    MASSACHUSETTS: 'MA',
    MICHIGAN: 'MI',
    MINNESOTA: 'MN',
    MISSISSIPPI: 'MS',
    MISSOURI: 'MO',
    MONTANA: 'MT',
    NEBRASKA: 'NE',
    NEVADA: 'NV',
    'NEW HAMPSHIRE': 'NH',
    'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM',
    'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC',
    'NORTH DAKOTA': 'ND',
    OHIO: 'OH',
    OKLAHOMA: 'OK',
    OREGON: 'OR',
    PENNSYLVANIA: 'PA',
    'RHODE ISLAND': 'RI',
    'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD',
    TENNESSEE: 'TN',
    TEXAS: 'TX',
    UTAH: 'UT',
    VERMONT: 'VT',
    VIRGINIA: 'VA',
    WASHINGTON: 'WA',
    'WEST VIRGINIA': 'WV',
    WISCONSIN: 'WI',
    WYOMING: 'WY',
    'DISTRICT OF COLUMBIA': 'DC',
  },
  CA: {
    ALBERTA: 'AB',
    'BRITISH COLUMBIA': 'BC',
    MANITOBA: 'MB',
    'NEW BRUNSWICK': 'NB',
    'NEWFOUNDLAND AND LABRADOR': 'NL',
    'NOVA SCOTIA': 'NS',
    'NORTHWEST TERRITORIES': 'NT',
    NUNAVUT: 'NU',
    ONTARIO: 'ON',
    'PRINCE EDWARD ISLAND': 'PE',
    QUEBEC: 'QC',
    SASKATCHEWAN: 'SK',
    YUKON: 'YT',
  },
}

const US_STATE_SALES_TAX_RATES: Record<string, number> = {
  AL: 4,
  AK: 0,
  AZ: 5.6,
  AR: 6.5,
  CA: 7.25,
  CO: 2.9,
  CT: 6.35,
  DE: 0,
  FL: 6,
  GA: 4,
  HI: 4,
  ID: 6,
  IL: 6.25,
  IN: 7,
  IA: 6,
  KS: 6.5,
  KY: 6,
  LA: 4.45,
  ME: 5.5,
  MD: 6,
  MA: 6.25,
  MI: 6,
  MN: 6.875,
  MS: 7,
  MO: 4.225,
  MT: 0,
  NE: 5.5,
  NV: 6.85,
  NH: 0,
  NJ: 6.625,
  NM: 5.125,
  NY: 4,
  NC: 4.75,
  ND: 5,
  OH: 5.75,
  OK: 4.5,
  OR: 0,
  PA: 6,
  RI: 7,
  SC: 6,
  SD: 4.2,
  TN: 7,
  TX: 6.25,
  UT: 6.1,
  VT: 6,
  VA: 5.3,
  WA: 6.5,
  WV: 6,
  WI: 5,
  WY: 4,
  DC: 6,
}

const CA_PROVINCE_TAX_RATES: Record<string, number> = {
  AB: 5,
  BC: 12,
  MB: 12,
  NB: 15,
  NL: 15,
  NS: 15,
  NT: 5,
  NU: 5,
  ON: 13,
  PE: 15,
  QC: 14.975,
  SK: 11,
  YT: 5,
}

const buildRegionalTaxRule = (label: string, percent: number, description: string): TaxRule => ({
  label,
  percent,
  inclusive: false,
  requiresAbn: false,
  description,
})

const REGION_TAX_RULES_BY_COUNTRY: Partial<Record<CountryCode, Record<string, TaxRule>>> = {
  US: Object.fromEntries(
    Object.entries(US_STATE_SALES_TAX_RATES).map(([region, percent]) => [
      region,
      buildRegionalTaxRule('Sales Tax', percent, 'State sales tax (local taxes may also apply)'),
    ])
  ),
  CA: Object.fromEntries(
    Object.entries(CA_PROVINCE_TAX_RATES).map(([region, percent]) => [
      region,
      buildRegionalTaxRule('HST/GST', percent, 'Province-level GST/HST/PST blended rate'),
    ])
  ),
}

const normalizeRegionCode = (country: CountryCode, region: unknown): string | null => {
  const raw = String(region || '').trim().toUpperCase()
  if (!raw) return null
  const aliases = REGION_CODE_ALIASES_BY_COUNTRY[country]
  return aliases ? aliases[raw] || raw : raw
}

type StripeFeeRule = {
  rate: number
  fixedCents: number
  label: string
}

const STRIPE_FEE_RULES_BY_COUNTRY: Record<CountryCode, StripeFeeRule> = {
  AU: { rate: 0.017, fixedCents: 30, label: '1.7% + 0.30 AUD' },
  NZ: { rate: 0.029, fixedCents: 30, label: '2.9% + 0.30 NZD' },
  US: { rate: 0.029, fixedCents: 30, label: '2.9% + 0.30 USD' },
  CA: { rate: 0.029, fixedCents: 30, label: '2.9% + 0.30 CAD' },
  GB: { rate: 0.015, fixedCents: 20, label: '1.5% + 0.20 GBP' },
  IE: { rate: 0.015, fixedCents: 20, label: '1.5% + 0.20 EUR' },
  SG: { rate: 0.034, fixedCents: 50, label: '3.4% + 0.50 SGD' },
  HK: { rate: 0.034, fixedCents: 230, label: '3.4% + 2.30 HKD' },
}

const FALLBACK_TIME_ZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'UTC',
  'Pacific/Auckland',
  'America/New_York',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Dublin',
  'Asia/Singapore',
  'Asia/Hong_Kong',
]

export const DEFAULT_TIME_ZONE = 'Australia/Sydney'

export const getTaxRuleForCountry = (value: unknown, region?: unknown): TaxRule => {
  const country = normalizeCountryCode(value)
  const base = TAX_RULES_BY_COUNTRY[country]
  if (country !== 'US' && country !== 'CA') return base
  const regionCode = normalizeRegionCode(country, region)
  const regional = regionCode ? REGION_TAX_RULES_BY_COUNTRY[country]?.[regionCode] : null
  return regional || base
}

export const getStripeFeeRuleForCountry = (value: unknown): StripeFeeRule =>
  STRIPE_FEE_RULES_BY_COUNTRY[normalizeCountryCode(value)]

export const detectTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return tz || DEFAULT_TIME_ZONE
  } catch {
    return DEFAULT_TIME_ZONE
  }
}

export const listTimeZoneOptions = (preferred: string[] = []): string[] => {
  const fromIntl =
    typeof Intl !== 'undefined' && typeof (Intl as any).supportedValuesOf === 'function'
      ? ((Intl as any).supportedValuesOf('timeZone') as string[])
      : []
  const merged = [...preferred, ...fromIntl, ...FALLBACK_TIME_ZONES].filter(Boolean)
  return Array.from(new Set(merged))
}

export const formatCurrencyAmount = (amount: number, currencyCode: string): string => {
  const currency = String(currencyCode || 'AUD').toUpperCase()
  if (!Number.isFinite(amount)) return `0.00 ${currency}`
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}
